import React from 'react';
import cx from 'classnames';
import * as styles from './BaseInput.module.css';

const RADIUS_CLASSES: Record<string, string> = {
  sm: 'radiusSm',
  md: 'radiusMd',
  lg: 'radiusLg',
};

interface BaseInputProps {
  variant?: 'outlined' | 'floated';
  fullWidth?: boolean;
  disabled?: boolean;
  startAdornment?: React.ReactNode;
  placeholder?: string | null;
  floatedPlaceholder?: boolean;
  endAdornment?: React.ReactNode;
  success?: boolean;
  error?: boolean;
  children: React.ReactNode;
  customPresentation?: boolean;
  inputProps?: Record<string, unknown>;
  value?: string;
  radius?: string | null;
}

function BaseInput({
  variant = 'outlined',
  fullWidth = true,
  disabled = false,
  startAdornment = null,
  placeholder = null,
  floatedPlaceholder = true,
  endAdornment = null,
  success = false,
  error = false,
  children,
  customPresentation = false,
  inputProps = {},
  value = '',
  radius = null,
}: BaseInputProps) {
  const hasValue = !!(value && value.length > 0);
  const radiusClass =
    radius && RADIUS_CLASSES[radius] ? styles[RADIUS_CLASSES[radius]] : null;
  const radiusStyle =
    radius && !RADIUS_CLASSES[radius] ? { borderRadius: radius } : {};

  return (
    <div
      data-testid="base-input"
      className={cx(styles.wrapper, styles[variant], radiusClass, {
        [styles.readOnly]: inputProps.readOnly,
        [styles.disabled]: disabled,
        [styles.error]: error,
        [styles.success]: success,
        [styles.noFloat]: !floatedPlaceholder,
        [styles.floatLabel]: floatedPlaceholder && !customPresentation,
        [styles.withStartAdornment]: !!startAdornment,
        [styles.withEndAdornment]: !!endAdornment,
        [styles.hasValue]: hasValue || customPresentation,
      })}
      style={{
        ...(inputProps.width || inputProps.height
          ? {
              width: fullWidth ? '100%' : (inputProps.width as string),
              height: inputProps.height as string,
            }
          : { width: fullWidth ? '100%' : undefined }),
        ...radiusStyle,
      }}
    >
      {startAdornment && (
        <div className={cx(styles.adornment, styles.adornmentStart)}>
          {startAdornment}
        </div>
      )}
      {children}
      {placeholder && floatedPlaceholder && (
        <label className={styles.label}>{placeholder}</label>
      )}
      {endAdornment && (
        <div className={cx(styles.adornment, styles.adornmentEnd)}>
          {endAdornment}
        </div>
      )}
    </div>
  );
}

export default BaseInput;
